<template>
  <div>
    <b-card no-body class="mb-1">
      <b-card-body class="d-flex align-items-center" style="gap: 1rem">
        <span>Сезон</span>
        <b-form-select v-model="season" :options="seasonOptions" style="width: 110px" @change="loadStandings" />
      </b-card-body>
    </b-card>

    <b-overlay :show="isLoading">
      <b-row>
        <!-- Группа A — одна на ряд, по центру; остальные — по две в ряд -->
        <b-col v-for="group in groups" :key="group.group_letter" lg="6" cols="12"
               :offset-lg="group.group_letter === 'A' ? '3' : null">
          <b-card>
            <div class="d-flex justify-content-between align-items-center mb-1">
              <span v-if="group.is_top_league" class="font-weight-bold text-primary">Топ-Лига</span>
              <span v-else />
              <b-button size="sm" variant="outline-primary" @click="saveImage(group)">
                Сохранить картинкой
              </b-button>
            </div>
            <b-table :items="groupRows(group)" :fields="groupFields(group)" small striped hover
                     empty-text="Нет игроков" show-empty>
              <template #cell(place_change)="data">
                <span :class="placeChangeClass(data.value)">{{ formatPlaceChange(data.value) }}</span>
              </template>
            </b-table>
          </b-card>
        </b-col>
      </b-row>

      <b-card v-if="playoff.length" title="Плей-офф">
        <div v-for="game in playoff" :key="game.name + game.scheduled_at" class="mb-1">
          <strong>{{ stageLabel(game.stage) }}{{ game.tour ? ` ${game.tour}` : '' }}</strong>
          <span class="text-muted ml-50">{{ formatDateTime(game.scheduled_at) }}</span>
          <div v-for="result in game.results" :key="result.place">
            {{ result.place }}. {{ result.name }} — {{ result.points }}
          </div>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BCard, BCardBody, BRow, BCol, BButton, BOverlay, BFormSelect, BTable,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
import store from '@/store'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { formatDateTime } from '@core/utils/filter'
import fnfLeagueStoreModule from '@/views/fnf-league/fnfLeagueStoreModule'
import exportLeagueTableImage from '@/views/fnf-league/exportLeagueTableImage'

const STORE_MODULE_NAME = 'app-fnf-league'

export default {
  components: {
    BCard, BCardBody, BRow, BCol, BButton, BOverlay, BFormSelect, BTable,
  },
  setup() {
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, fnfLeagueStoreModule)
    onUnmounted(() => { if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME) })

    const toast = useToast()
    const showError = title => toast({ component: ToastificationContent, props: { title, icon: 'AlertTriangleIcon', variant: 'danger' } })

    const isLoading = ref(false)
    const season = ref(null)
    const seasonOptions = ref([])
    const groups = ref([])
    const playoff = ref([])

    const loadStandings = () => {
      isLoading.value = true
      store.dispatch(`${STORE_MODULE_NAME}/fetchStandings`, { season: season.value })
        .then(response => {
          groups.value = response.data.groups
          playoff.value = response.data.playoff
          season.value = response.data.season
          seasonOptions.value = response.data.seasons.map(value => ({ value, text: `Сезон ${value}` }))
        })
        .catch(() => showError('Ошибка загрузки таблиц'))
        .finally(() => { isLoading.value = false })
    }

    // Сдвиг мест приходит с бэкенда только когда сыграно >1 тура
    // и у всех игроков поровну игр — иначе place_change = null и колонки нет
    const hasPlaceChanges = group => group.rows.some(row => row.place_change !== null)

    const groupFields = group => [
      { key: 'place', label: '#' },
      ...(hasPlaceChanges(group) ? [{ key: 'place_change', label: '±', class: 'text-center' }] : []),
      { key: 'name', label: `Группа ${group.group_letter}${group.is_top_league ? ' · Топ-Лига' : ''} / Игрок` },
      { key: 'games_played', label: 'Игр', class: 'text-center' },
      { key: 'first_places', label: 'Побед', class: 'text-center' },
      { key: 'second_places', label: '2-х мест', class: 'text-center' },
      { key: 'third_places', label: '3-х мест', class: 'text-center' },
      { key: 'fourth_places', label: '4-х мест', class: 'text-center' },
      { key: 'total_points', label: 'Баллов', class: 'text-center font-weight-bold' },
      { key: 'total_score', label: 'Сумма очков', class: 'text-right' },
    ]

    // Подсветка проходящих дальше мест — по правилам сезона с бэкенда
    // (qualifying_places: 4 в топ-4 группах/группе A с сезона 2, 1 — в остальных с сезона 2);
    // fallback на 4, если бэкенд ещё не задеплоен с новым полем (рассинхрон деплоя)
    const isHighlighted = (group, row) => row.place <= (group.qualifying_places || 4)

    const groupRows = group => group.rows.map(row => ({
      place: row.place,
      place_change: row.place_change,
      name: row.name,
      games_played: row.games_played,
      first_places: row.first_places,
      second_places: row.second_places,
      third_places: row.third_places,
      fourth_places: row.fourth_places,
      total_points: row.total_points,
      total_score: row.total_score,
      _rowVariant: isHighlighted(group, row) ? 'primary' : null,
    }))

    const saveImage = group => {
      exportLeagueTableImage({ season: season.value, group })
        .catch(() => showError('Не удалось сохранить картинку'))
    }

    const stageLabel = stage => ({ semifinal: 'Полуфинал', final: 'Финал' }[stage] || stage)

    const formatPlaceChange = change => {
      if (change === null) return ''
      if (change > 0) return `+${change}`
      if (change < 0) return String(change)
      return '='
    }

    const placeChangeClass = change => {
      if (change > 0) return 'text-success font-weight-bold'
      if (change < 0) return 'text-danger font-weight-bold'
      return 'text-muted'
    }

    loadStandings()

    return {
      isLoading, season, seasonOptions, groups, playoff,
      loadStandings, groupFields, groupRows, saveImage, stageLabel, formatDateTime,
      formatPlaceChange, placeChangeClass,
    }
  },
}
</script>
